import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ChevronRight } from 'lucide-react';
import { useAuth, AgencyOption } from '../context/AuthContext';

const SelectAgencyPage: React.FC = () => {
  const { agencies, switchAgency } = useAuth();
  const navigate = useNavigate();
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [loading, setLoading] = useState<string | null>(null);

  const handleSelect = async (agency: AgencyOption) => {
    setLoading(agency.agency_id);
    await switchAgency(agency.agency_id);
    navigate('/dashboard');
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'var(--v2-bg)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'var(--font-sans)',
      padding: '24px',
    }}>
      <div style={{ width: '100%', maxWidth: '480px' }}>
        <h1 style={{
          fontSize: '1.5rem',
          fontWeight: 700,
          color: 'var(--v2-text)',
          margin: '0 0 8px 0',
          textAlign: 'center',
        }}>
          Select an Agency
        </h1>
        <p style={{
          fontSize: '0.9375rem',
          color: 'var(--v2-text-muted)',
          textAlign: 'center',
          margin: '0 0 32px 0',
        }}>
          You belong to multiple agencies. Which one would you like to enter?
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {agencies.map((agency) => {
            const isHovered = hoveredId === agency.agency_id;
            const isLoading = loading === agency.agency_id;
            return (
              <button
                key={agency.agency_id}
                onClick={() => handleSelect(agency)}
                disabled={!!loading}
                onMouseEnter={() => setHoveredId(agency.agency_id)}
                onMouseLeave={() => setHoveredId(null)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  padding: '16px 20px',
                  backgroundColor: isHovered ? 'var(--v2-surface-hover)' : 'var(--v2-surface)',
                  border: '1px solid var(--v2-border)',
                  borderRadius: 'var(--radius-lg)',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.15s',
                  opacity: loading && !isLoading ? 0.5 : 1,
                }}
              >
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: agency.primary_color || 'var(--v2-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {agency.logo_small_url ? (
                    <img src={agency.logo_small_url} alt={agency.name} style={{ width: '24px', height: '24px', objectFit: 'contain' }} />
                  ) : (
                    <Building2 size={20} color="#ffffff" strokeWidth={1.5} />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '0.9375rem',
                    fontWeight: 600,
                    color: 'var(--v2-text)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {agency.name}
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: 'var(--v2-text-muted)',
                    marginTop: '2px',
                    textTransform: 'capitalize',
                  }}>
                    {agency.role.replace('_', ' ')}
                  </div>
                </div>
                {isLoading ? (
                  <div style={{
                    width: '18px', height: '18px',
                    border: '2px solid var(--v2-border)',
                    borderTopColor: 'var(--v2-primary)',
                    borderRadius: '50%',
                    animation: 'spin 0.7s linear infinite',
                    flexShrink: 0,
                  }} />
                ) : (
                  <ChevronRight size={18} strokeWidth={1.5} color="var(--v2-text-muted)" />
                )}
              </button>
            );
          })}
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default SelectAgencyPage;
